import type { ServerNode } from "./types";
import type { ActionNodeData } from "../nodes/ActionNode";
import { ACTION_CODE_TO_FORM_TYPE, actionCodeToTitle } from "../utils/actionCodeMapping";
import { castToString, castToStringArray } from "./configCastHelpers";

/**
 * Converts a server Action.* node into an ActionNodeData object
 * that the existing ActionNode component and ActionConfigPanel can consume.
 *
 * Each action code maps to a legacy `actionType` (e.g. "AutomatedResponse")
 * via ACTION_CODE_TO_FORM_TYPE so the existing form-rendering switch logic works.
 *
 * Field-name translations (server → UI):
 *   config.customEmails        → additionalEmails
 *   config.shouldThreadActivities → logActivityMergeActivities
 */
export function hydrateActionData(node: ServerNode): ActionNodeData {
    const { config, type } = node;
    const formType = ACTION_CODE_TO_FORM_TYPE[type];

    const base: ActionNodeData = {
        actionCode: type,
        actionType: formType ?? type,
        title: actionCodeToTitle(type) ?? "Action",
        isPlaceholder: false,
    };

    switch (type) {
        case "Action.Message.AutomatedResponse":
            return {
                ...base,
                message: castToString(config.messageBody ?? config.message),
                templateId: castToString(config.templateId),
                mediaFileList: Array.isArray(config.mediaFileList) ? config.mediaFileList : [],
                components: config.components,
                fieldNameAndValueMap:
                    config.fieldNameAndValueMap != null && typeof config.fieldNameAndValueMap === "object"
                        ? (config.fieldNameAndValueMap as Record<string, unknown>)
                        : undefined,
            };

        case "Action.Crm.CreateLead":
            return { ...base };

        case "Action.Crm.AddNote":
            return {
                ...base,
                logRecordsFor: castToString(config.note) ?? "all",
                logRecordAsThread: config.shouldThreadActivities === true ? "true" : "false",
            };

        case "Action.Crm.LogActivity":
            return {
                ...base,
                logRecordsFor: castToString(config.note) ?? "all",
                logRecordAsThread: config.shouldThreadActivities === true ? "true" : "false",
            };

        case "Action.Notify.NotifyUser":
            return {
                ...base,
                recordOwner: config.includeRecordOwner === true,
                accountOwner: config.includeAccountOwner === true,
                includeAdditionalEmails: (castToStringArray(config.customEmails)?.length ?? 0) > 0,
                additionalEmails: castToStringArray(config.customEmails),
            };

        default:
            return base;
    }
}
